import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ShieldOff, Trash2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/**
 * Очередь жалоб. Apple (Guideline 1.2) требует реакции за 24 часа, поэтому
 * просроченные вынесены отдельным счётчиком наверх — это единственная метрика,
 * по которой видно, держим мы обещание или нет.
 */

type ReportStatus = 'open' | 'resolved' | 'dismissed';
type ReportAction = 'content_removed' | 'user_banned' | 'content_removed_and_user_banned' | 'none';

interface ReportRow {
  id: string;
  targetType: string;
  targetId: string;
  reason: string;
  comment: string | null;
  snapshot: string | null;
  status: ReportStatus;
  action: ReportAction | null;
  resolutionNote: string | null;
  resolvedAt: string | null;
  createdAt: string;
  reporterId: string;
  targetUserId: string | null;
  targetUsername: string | null;
  targetFirstName: string | null;
  targetBannedAt: string | null;
}

interface ReportsResponse {
  data: ReportRow[];
  meta: { openCount: number; overdueCount: number };
}

const REASON_RU: Record<string, string> = {
  abuse: 'Оскорбления',
  harassment: 'Травля',
  spam: 'Спам',
  sexual: 'Сексуализированный контент',
  violence: 'Насилие, угрозы',
  illegal: 'Противоправное',
  other: 'Другое',
};

const TARGET_RU: Record<string, string> = {
  chat_message: 'сообщение в чате',
  user: 'профиль',
  venue_review: 'отзыв о площадке',
  trainer_review: 'отзыв о тренере',
  intent_comment: 'комментарий',
  player_intent: 'объявление',
  activity: 'игра',
  organization: 'клуб',
};

function hoursSince(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / 36e5;
}

function formatWhen(iso: string): string {
  try { return new Date(iso).toLocaleString('ru'); } catch { return iso; }
}

export function ReportsPage() {
  const [status, setStatus] = useState<ReportStatus | 'all'>('open');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<ReportsResponse>({
    queryKey: ['admin-reports', status],
    queryFn: () => api.get<ReportsResponse>(`/api/admin/reports?status=${status}&limit=200`),
    refetchInterval: 60_000,
  });

  const resolve = useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: ReportAction; note?: string }) =>
      api.post(`/api/admin/reports/${id}/resolve`, { action, note }),
    onSuccess: async () => {
      toast.success('Жалоба закрыта');
      await qc.invalidateQueries({ queryKey: ['admin-reports'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = data?.data ?? [];
  const overdue = data?.meta.overdueCount ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-zinc-100">Жалобы</h1>
        <div className="flex gap-2">
          {(['open', 'resolved', 'dismissed', 'all'] as const).map((s) => (
            <Button
              key={s}
              variant={status === s ? 'default' : 'secondary'}
              size="sm"
              onClick={() => setStatus(s)}
            >
              {s === 'open' ? 'Открытые' : s === 'resolved' ? 'Решённые' : s === 'dismissed' ? 'Отклонённые' : 'Все'}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-zinc-400">Открытых</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold text-zinc-100">{data?.meta.openCount ?? '—'}</CardContent>
        </Card>
        <Card className={overdue > 0 ? 'border-red-500/50' : undefined}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm text-zinc-400">
              {overdue > 0 && <AlertTriangle className="h-4 w-4 text-red-500" />}
              Просрочено (&gt; 24 ч)
            </CardTitle>
          </CardHeader>
          <CardContent className={`text-2xl font-bold ${overdue > 0 ? 'text-red-400' : 'text-zinc-100'}`}>
            {overdue}
          </CardContent>
        </Card>
      </div>

      {isLoading && <div className="p-6 text-zinc-400">Загрузка…</div>}
      {!isLoading && rows.length === 0 && (
        <div className="rounded-lg border border-zinc-800 p-8 text-center text-zinc-400">
          Жалоб нет
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => {
          const age = hoursSince(r.createdAt);
          const isOverdue = r.status === 'open' && age > 24;
          const busy = resolve.isPending;
          return (
            <Card key={r.id} className={isOverdue ? 'border-red-500/50' : undefined}>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base text-zinc-100">
                    {REASON_RU[r.reason] ?? r.reason}
                  </CardTitle>
                  <span className="text-sm text-zinc-500">
                    на {TARGET_RU[r.targetType] ?? r.targetType}
                  </span>
                  {isOverdue && (
                    <span className="rounded bg-red-500/15 px-2 py-0.5 text-xs font-semibold text-red-400">
                      просрочено {Math.floor(age)} ч
                    </span>
                  )}
                  {r.targetBannedAt && (
                    <span className="rounded bg-zinc-700 px-2 py-0.5 text-xs text-zinc-300">
                      автор уже забанен
                    </span>
                  )}
                  <span className="ml-auto text-xs text-zinc-500">{formatWhen(r.createdAt)}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {r.snapshot && (
                  <blockquote className="rounded border-l-2 border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-300">
                    {r.snapshot}
                  </blockquote>
                )}
                {r.comment && (
                  <p className="text-sm text-zinc-400">
                    <span className="text-zinc-500">Комментарий: </span>{r.comment}
                  </p>
                )}
                <p className="text-xs text-zinc-500">
                  Автор контента:{' '}
                  {r.targetUserId
                    ? <a className="underline" href={`/users/${r.targetUserId}`}>
                        {r.targetFirstName ?? '—'} @{r.targetUsername ?? '—'}
                      </a>
                    : '—'}
                </p>

                {r.status === 'open' ? (
                  <>
                    <Input
                      placeholder="Заметка о решении (необязательно)"
                      value={notes[r.id] ?? ''}
                      onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm" disabled={busy}
                        onClick={() => resolve.mutate({ id: r.id, action: 'content_removed', note: notes[r.id] })}
                      >
                        <Trash2 className="mr-1 h-4 w-4" /> Удалить контент
                      </Button>
                      <Button
                        size="sm" variant="secondary" disabled={busy || !r.targetUserId}
                        onClick={() => resolve.mutate({ id: r.id, action: 'content_removed_and_user_banned', note: notes[r.id] })}
                      >
                        <ShieldOff className="mr-1 h-4 w-4" /> Удалить и забанить
                      </Button>
                      <Button
                        size="sm" variant="secondary" disabled={busy || !r.targetUserId}
                        onClick={() => resolve.mutate({ id: r.id, action: 'user_banned', note: notes[r.id] })}
                      >
                        <ShieldOff className="mr-1 h-4 w-4" /> Только забанить
                      </Button>
                      <Button
                        size="sm" variant="ghost" disabled={busy}
                        onClick={() => resolve.mutate({ id: r.id, action: 'none', note: notes[r.id] })}
                      >
                        <X className="mr-1 h-4 w-4" /> Нарушения нет
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="flex items-center gap-2 text-sm text-zinc-400">
                    <Check className="h-4 w-4 text-emerald-500" />
                    {r.action === 'none' ? 'Нарушения не найдено' : `Решение: ${r.action}`}
                    {r.resolutionNote ? ` — ${r.resolutionNote}` : ''}
                    {r.resolvedAt ? ` (${formatWhen(r.resolvedAt)})` : ''}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
